import { hasValue } from "@utils";
// Helper functions
function isExactPathMatch(linkPath) {
    const currentPath = getNormalizedCurrentPath();
    const normalizedLink = normalizePath(linkPath);
    return currentPath === normalizedLink;
}
function matchesDynamicRouteBase(linkPath) {
    const currentPath = getNormalizedCurrentPath();
    const normalizedLink = normalizePath(linkPath);
    // Handle dynamic routes like /view/location/details/:key
    if (normalizedLink === null || normalizedLink === void 0 ? void 0 : normalizedLink.includes('/:')) {
        const basePath = normalizedLink.split('/:')[0];
        return currentPath.startsWith(basePath + '/');
    }
    // Handle cases like /view/location matching /view/location/details/123
    return currentPath.startsWith(normalizedLink + '/');
}
function getNormalizedCurrentPath() {
    const { pathname } = window.location;
    return normalizePath(pathname);
}
function normalizePath(path) {
    var _a, _b;
    return (_b = (_a = path === null || path === void 0 ? void 0 : path.replace(/^\s+|\s+$/g, '') // Trim whitespace
    ) === null || _a === void 0 ? void 0 : _a.replace(/^\/+|\/+$/g, '') // Trim leading/trailing slashes
    ) === null || _b === void 0 ? void 0 : _b.toLowerCase(); // Convert to lowercase
}
export function isActiveLink(link, selectedItem, ignoreSelectedChildren) {
    var _a;
    if (!!selectedItem)
        return ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id) === link.id);
    if (isExactPathMatch(link.link))
        return true;
    if (matchesDynamicRouteBase(link.link))
        return true;
    if (((_a = link.children) === null || _a === void 0 ? void 0 : _a.length) && !ignoreSelectedChildren) {
        return link.children.some(child => {
            var _a;
            return isExactPathMatch(child.link) ||
                matchesDynamicRouteBase(child.link) ||
                ((_a = child.children) === null || _a === void 0 ? void 0 : _a.some(cc => isActiveLink(cc)));
        });
    }
    return false;
}
export function processNavigationLinks(context, isAnonymous) {
    // assume sidebar items are formatted NavigationLink format
    // doing a type cast
    // need to handle items registered through code
    // const items = _.cloneDeep(((env == 'dev') ? getSidebarItemsInDev() : context?.navigationLinks || []) as unknown[]) as NavigationLink[];
    const items = (context === null || context === void 0 ? void 0 : context.navigationLinks) || [];
    return splitNavigationLinks(context, items, isAnonymous);
}
export function splitNavigationLinks(context, links, isAnonymous) {
    const top = {};
    const filterAllowed = (items) => {
        // Backend now returns only the links this user is allowed to see (profile-filtered for
        // authenticated users, public-only for anonymous). No client-side access checks needed.
        return items.map(item => (Object.assign(Object.assign({}, item), { children: item.children ? filterAllowed(item.children) : [] })));
    };
    const allowedLinks = filterAllowed(links);
    const resolveFirstLeafLink = (items) => {
        for (const item of items) {
            if (hasValue(item.link))
                return item.link;
            const nested = resolveFirstLeafLink(item.children || []);
            if (nested)
                return nested;
        }
        return null;
    };
    const resolveGroupLinks = (items) => {
        return items.map(item => {
            var _a, _b;
            const processedChildren = ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length)
                ? resolveGroupLinks(item.children)
                : item.children;
            if (!hasValue(item.link) && (processedChildren === null || processedChildren === void 0 ? void 0 : processedChildren.length)) {
                return Object.assign(Object.assign({}, item), { children: processedChildren, link: (_b = resolveFirstLeafLink(processedChildren)) !== null && _b !== void 0 ? _b : item.link });
            }
            return Object.assign(Object.assign({}, item), { children: processedChildren });
        });
    };
    const linksToProcess = isAnonymous ? resolveGroupLinks(allowedLinks) : allowedLinks;
    const flatten = flattenSidebarLinks(linksToProcess);
    const getParent = (item) => {
        return flatten.find(l => l.id === item.parentId) || null;
    };
    const process = (items, level, parentId = null) => {
        return items.map(item => {
            const { children } = item;
            const newItem = Object.assign(Object.assign({}, item), { parentId });
            if (children === null || children === void 0 ? void 0 : children.length) {
                if (level < 3) {
                    newItem.children = process(children, level + 1, item.id);
                }
                else if (level === 3) {
                    top[item.id] = {
                        parent: getParent(newItem),
                        children: children.map(child => (Object.assign(Object.assign({}, child), { parentId: item.id }))),
                        link: newItem
                    };
                }
            }
            return newItem;
        });
    };
    const sidebar = process(linksToProcess, 1);
    return { sidebar, top, all: links };
}
export function isGroup(link) {
    var _a;
    return (link === null || link === void 0 ? void 0 : link.type) === 'group' || (!!(link === null || link === void 0 ? void 0 : link.children) && ((_a = link.children) === null || _a === void 0 ? void 0 : _a.length) > 0);
}
export function hasGroupedLinks(items) {
    return items === null || items === void 0 ? void 0 : items.some(l => isGroup(l));
}
export function flattenSidebarLinks(links) {
    const flatten = [];
    const collect = (items) => {
        var _a;
        for (const item of items) {
            flatten.push(Object.assign({}, item));
            if ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) {
                collect(item.children);
            }
        }
    };
    if (links === null || links === void 0 ? void 0 : links.length) {
        collect(links);
    }
    return flatten;
}
export function getActiveTopNavigationLink(topNavigation) {
    const activeLink = Object.keys(topNavigation).find(linkId => {
        return isActiveLink(topNavigation[linkId].link);
    });
    if (hasValue(activeLink))
        return (topNavigation === null || topNavigation === void 0 ? void 0 : topNavigation[activeLink]) || null;
    return null;
}
/**
 * Converts dashboard sidebar items (PortalLink[]) to TopNavigationLinks format
 * For dashboards, the dashboard title acts as the parent with sidebar items as children
 * @param dashboardTitle - Title of the dashboard
 * @param dashboardId - ID of the dashboard
 * @param parsedSidebarItems - Sidebar items from dashboard details (PortalLink[])
 * @returns TopNavigationLinks structure for rendering
 */
export function convertDashboardSidebarToTopNavigation(dashboardConfig) {
    const { details: { Title, ID, ParsedSidebarItems } } = dashboardConfig;
    // Convert PortalLink (old format) or DashboardNavLink (new format) to NavigationLink.
    // Old format uses PascalCase fields + a nested Configuration object.
    // New format (from NavLinksEditor) uses camelCase fields directly on the item.
    const convertToNavigationLink = (item, parentLink) => {
        const { ID, Roles, Label, Icon, Configuration } = item;
        // New format detection: has lowercase 'id' but no 'Configuration' or 'ID'
        const isNewFormat = !Configuration && !ID && hasValue(item.id);
        const resolvedId = isNewFormat ? item.id : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.id) || ID || '');
        const resolvedLabel = isNewFormat ? item.label : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.label) || Label || '');
        const resolvedIcon = isNewFormat ? (item.icon || '') : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.icon) || Icon || '');
        const resolvedRoles = isNewFormat ? (item.roles || '') : (Roles || '');
        let link;
        if (isNewFormat) {
            link = item.type === 'group' ? null : (item.link || '#');
        }
        else {
            link = (Configuration === null || Configuration === void 0 ? void 0 : Configuration.isGrouped)
                ? null
                : hasValue(Configuration === null || Configuration === void 0 ? void 0 : Configuration.dashboardKey)
                    ? `${parentLink}/${Configuration.dashboardKey}`
                    : (Configuration === null || Configuration === void 0 ? void 0 : Configuration.link) || '#';
        }
        const children = item.Children && item.Children.length > 0
            ? item.Children.map((child) => convertToNavigationLink(child, parentLink))
            : [];
        const target = isNewFormat
            ? (item.type === 'externalLink' ? '_blank' : undefined)
            : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.target) || undefined);
        return Object.assign({ id: resolvedId, label: resolvedLabel, icon: resolvedIcon, link: link !== null && link !== void 0 ? link : '#', type: isNewFormat ? (item.type || undefined) : undefined, userGroups: resolvedRoles.split(',').filter(Boolean), appRoles: [], children }, (target ? { target } : {}));
    };
    // Create parent navigation link representing the dashboard
    const parentLink = {
        id: `dashboard-${ID}`,
        label: Title,
        icon: 'dashboard',
        link: `/dashboard/${ID}`,
        userGroups: [],
        appRoles: []
    };
    // Convert all sidebar items to NavigationLink format.
    // Old format: flat list of items with nested Children arrays.
    // New format (NavLinksEditor): flat list with parentID — build tree first.
    let topLevelItems = [];
    if (ParsedSidebarItems && ParsedSidebarItems.length > 0) {
        const firstItem = ParsedSidebarItems[0];
        const isNewFormat = !firstItem.Configuration && !firstItem.ID && hasValue(firstItem.id);
        if (isNewFormat) {
            // Build tree from flat list using parentID
            const sorted = [...ParsedSidebarItems].sort((a, b) => (a.priority || 0) - (b.priority || 0));
            const byId = {};
            sorted.forEach(item => { byId[item.id] = Object.assign(Object.assign({}, item), { Children: [] }); });
            sorted.forEach(item => {
                if (item.parentID && byId[item.parentID]) {
                    byId[item.parentID].Children.push(byId[item.id]);
                }
            });
            topLevelItems = sorted.filter(item => !item.parentID).map(item => byId[item.id]);
        }
        else {
            topLevelItems = ParsedSidebarItems;
        }
    }
    const children = topLevelItems.length > 0
        ? topLevelItems.map(item => convertToNavigationLink(item, parentLink.link))
        : [];
    // Create TopNavigationLinks structure
    const topNavigation = {
        [parentLink.id]: {
            parent: null, // No parent for dashboard items
            children: children,
            link: parentLink
        }
    };
    return topNavigation;
}
